import { useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ActorUsersPanel from './ActorUsersPanel';
import type { ActorScope, ActorWithCount } from '../../services/api/actorService';
import { getActorDisplayName } from '../../utils/roleUtils';

const SCOPE_STYLES: Record<string, string> = {
    global: 'bg-blue-500/15 text-blue-400',
    department: 'bg-emerald-500/15 text-emerald-400',
    manufacturer: 'bg-purple-500/15 text-purple-400',
    nordinsight: 'bg-amber-500/15 text-amber-400',
};

const SCOPE_LABELS: Record<string, string> = {
    global: 'Global',
    department: 'Dept',
    manufacturer: 'Mfr',
    nordinsight: 'NI',
};

function ScopeBadge({ scope }: { scope?: ActorScope }) {
    if (!scope) return null;
    return (
        <span
            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${SCOPE_STYLES[scope] ?? 'bg-muted text-muted-foreground'}`}
        >
            {SCOPE_LABELS[scope] ?? scope}
        </span>
    );
}

interface ActorTableProps {
    actors: ActorWithCount[];
    activeDeptsOnly: boolean;
    /** manageEndUsers-only callers: rights hidden, delete disabled */
    limited?: boolean;
    onEdit: (actor: ActorWithCount) => void;
    onDelete: (actor: ActorWithCount) => void;
}

const MAX_BADGES = 3;

function BadgeList({ items }: { items: string[] }) {
    const visible = items.slice(0, MAX_BADGES);
    const remaining = items.length - MAX_BADGES;
    return (
        <div className="flex flex-wrap gap-1">
            {visible.map((item) => (
                <Badge
                    key={item}
                    variant="outline"
                    className="text-[10px] px-1.5 py-0"
                >
                    {item}
                </Badge>
            ))}
            {remaining > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    +{remaining}
                </Badge>
            )}
        </div>
    );
}

export default function ActorTable({
    actors,
    activeDeptsOnly,
    limited,
    onEdit,
    onDelete,
}: ActorTableProps) {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Role</TableHead>
                    {!limited && (
                        <TableHead className="hidden md:table-cell">
                            Rights
                        </TableHead>
                    )}
                    <TableHead className="hidden lg:table-cell">
                        Dept Permissions
                    </TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead className="w-24" />
                </TableRow>
            </TableHeader>
            <TableBody>
                {actors.map((actor) => {
                    const isExpanded = expandedId === actor._id;
                    return (
                        <ActorRow
                            key={actor._id}
                            actor={actor}
                            activeDeptsOnly={activeDeptsOnly}
                            limited={limited}
                            isExpanded={isExpanded}
                            onToggleExpand={() =>
                                setExpandedId(
                                    isExpanded ? null : actor._id
                                )
                            }
                            onEdit={() => onEdit(actor)}
                            onDelete={() => onDelete(actor)}
                        />
                    );
                })}
                {actors.length === 0 && (
                    <TableRow>
                        <TableCell
                            colSpan={6}
                            className="py-8 text-center text-muted-foreground"
                        >
                            No actors found.
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
}

function ActorRow({
    actor,
    activeDeptsOnly,
    limited,
    isExpanded,
    onToggleExpand,
    onEdit,
    onDelete,
}: {
    actor: ActorWithCount;
    activeDeptsOnly: boolean;
    limited?: boolean;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    return (
        <>
            <TableRow
                className="cursor-pointer hover:bg-muted/50"
                onClick={onToggleExpand}
            >
                <TableCell className="px-2">
                    {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                        <ChevronRight className="h-4 w-4" />
                    )}
                </TableCell>
                <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                        {getActorDisplayName(actor)}
                        <ScopeBadge scope={actor.scope} />
                    </div>
                </TableCell>
                {!limited && (
                    <TableCell className="hidden md:table-cell">
                        <BadgeList items={actor.rights} />
                    </TableCell>
                )}
                <TableCell className="hidden lg:table-cell">
                    {actor.deptPermissions.length > 0 ? (
                        <BadgeList items={actor.deptPermissions} />
                    ) : (
                        <span className="text-xs text-muted-foreground">
                            None
                        </span>
                    )}
                </TableCell>
                <TableCell className="text-right">{actor.userCount}</TableCell>
                <TableCell>
                    <div
                        className="flex justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Button
                            size="icon"
                            variant="ghost"
                            onClick={onEdit}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                        {!limited && (
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={onDelete}
                                disabled={actor.userCount > 0}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </TableCell>
            </TableRow>
            {isExpanded && (
                <TableRow>
                    <TableCell
                        colSpan={limited ? 5 : 6}
                        className="bg-muted/30 p-4"
                    >
                        <ActorUsersPanel
                            actorId={actor._id}
                            activeDeptsOnly={activeDeptsOnly}
                        />
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}
